// The price a search card shows when it cannot be priced from a calendar or a live quote:
// the villa's base price for the stay, as the SELLING price (net x markup, Util/pricingPolicy),
// with the agent's commission on that price (Util/pricingPolicy commissionOfTotal).
//
// The base price is in US dollars (prices.basePriceUSD) or, for a villa with no dollar base,
// in the villa's own currency (prices.basePrice + prices.currency). The card prints the
// number with the SELECTED currency's symbol, so it has to be converted into that currency
// first. It was not: the same search read $2,247, then EUR 2,247, then ILS 2,247.
import { commissionOfTotal, commissionFromNet, dividerFor } from "./pricingPolicy";

// The table the app keeps in localStorage ("exchange"): rows of
// { currency_code, conversion_rates }, conversion_rates being units per US dollar.
function storedRates() {
  try {
    const rows = JSON.parse(localStorage.getItem("exchange") || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch (e) {
    return [];
  }
}

function rateOf(rates, code) {
  const row = rates.find((r) => r && String(r.currency_code).toUpperCase() === code);
  const rate = Number(row && row.conversion_rates);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

// amount in `from` -> the same amount in `to`. The same currency, an unknown one, or a table
// with no usable rate for either side: the amount comes back as it went in, never zero.
export function convertCurrency(amount, from, to, exchangeRates) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 0;
  const source = String(from || "").toUpperCase();
  const target = String(to || "").toUpperCase();
  if (!source || !target || source === target) return value;
  const rates = Array.isArray(exchangeRates) ? exchangeRates : storedRates();
  const sourceRate = rateOf(rates, source);
  const targetRate = rateOf(rates, target);
  if (!sourceRate || !targetRate) return value;
  return value * (targetRate / sourceRate);
}

// { totalAmount, agencyCommission } in the display currency, for the card;
// { totalAmountRES, agencyCommissionRES } net and unconverted, as the reservation figures
// have always been.
export function basePriceStay({ prices, accountId, nights, displayCurrency, exchangeRates } = {}) {
  const usd = Number(prices && prices.basePriceUSD);
  const own = Number(prices && prices.basePrice);
  const hasUsd = Number.isFinite(usd) && usd > 0;
  const base = hasUsd ? usd : (Number.isFinite(own) && own > 0 ? own : 0);
  const sourceCurrency = hasUsd ? "USD" : String((prices && prices.currency) || "").toUpperCase();
  const stayNights = Math.max(1, Number(nights) || 1);
  const net = base * stayNights;
  const divider = dividerFor(accountId);
  const toDisplay = (amount) => convertCurrency(amount, sourceCurrency, displayCurrency, exchangeRates);
  return {
    totalAmount: toDisplay(net / divider),
    agencyCommission: toDisplay(commissionOfTotal(net / divider)),
    totalAmountRES: net,
    agencyCommissionRES: commissionFromNet(net, divider),
    sourceCurrency,
  };
}
